/**
 * Who may open which settings page (D-12, docs/frontend-workspace-settings-proposal.md §3 in
 * the SVN repo):
 *
 *   admin      Administrator, or Workspace Admin here   every page
 *   userAdmin  User Admin here                          Overview, People & seats
 *   member     anyone else                              Overview
 *
 * "Consultant" on its own is a member here, as the rest of the client already has it
 * (authorizationStore.userRoles drops it unless Workspace Admin is there too, which is what
 * keeps "Manage workspace" from a consultant-only member), and as billing has it (Workspace
 * Admin only). Offering those pages to a consultant would only show them refusals.
 *
 * plus "Your account", which is the person's own and open to everyone.
 *
 * The role is the one in THIS workspace: the `roles` on the workspace payload, which the server
 * builds from this workspace's membership row. Not authorizationStore's userRoles on its own -
 * the profile call overwrites that list with the ACCOUNT's roles, which say "Workspace Admin"
 * for anyone who is one anywhere. That is the mistake four server endpoints made until r33646.
 *
 * This keeps people off pages that would refuse them. The refusing is the server's job, and it
 * does it: billing, the settings read and every write check the role in the workspace.
 */
import { clientStore } from "../store/clientStore";
import { settingsGroupOf } from "./settingsNav";

export const SettingsRole = {
  ADMIN: "admin",
  USER_ADMIN: "userAdmin",
  MEMBER: "member",
};

const RANK = { member: 0, userAdmin: 1, admin: 2 };
const ADMIN_ROLES = ["Administrator", "Workspace Admin"];

/** The settings role from the role names on the workspace payload. */
export function roleFrom(roles) {
  const list = Array.isArray(roles) ? roles : [];
  if (ADMIN_ROLES.some(role => list.indexOf(role) >= 0)) return SettingsRole.ADMIN;
  if (list.indexOf("User Admin") >= 0) return SettingsRole.USER_ADMIN;
  return SettingsRole.MEMBER;
}

/**
 * The least a page asks for: the person's own pages ask for nothing, a page may name what it
 * needs with `access`, and everything else is the workspace's and asks for an admin.
 */
export function accessOf(item, group) {
  if (group && group.scope === "account") return SettingsRole.MEMBER;
  return (item && item.access) || SettingsRole.ADMIN;
}

/** May someone with `role` open a page that asks for `access`? Unknown names fail closed. */
export function roleAllows(access, role) {
  const need = access in RANK ? RANK[access] : RANK.admin;
  const have = role in RANK ? RANK[role] : RANK.member;
  return have >= need;
}

/** The signed-in person's role in the workspace that is open. */
export function settingsRole() {
  const workspace = clientStore.state && clientStore.state.workspace;
  return roleFrom(workspace && workspace.roles);
}

/** True when this nav item is theirs to open by role (the release stage is settingsStage.js). */
export function settingsRoleAllowed(item) {
  if (!item) return false;
  return roleAllows(accessOf(item, settingsGroupOf(item.id)), settingsRole());
}

export default { SettingsRole, roleFrom, accessOf, roleAllows, settingsRole, settingsRoleAllowed };
